import { cn } from '@mdvip/shared/lib/utils';
import { useTranslation } from 'react-i18next';
import { TransactionsTable } from '@/components/payments/transactions/TransactionsTable';
import { usePaymentTransactions } from '@/hooks/payments/transactions/usePaymentTransactions';

/** The dashboard preview shows the three most recent payments; the full history lives on its own tab. */
const RECENT_LIMIT = 3;

interface RecentTransactionsSectionProps {
  /** Switches the Payments page to its Past Transactions tab. */
  onViewAll: () => void;
  className?: string;
}

/**
 * Recent Transactions on the Payment Center dashboard: the same table the Past Transactions page
 * uses, in its compact mode. Owns its own query, like the other dashboard sections.
 *
 * Errors here are deliberately quiet. This is a summary alongside other cards, not the member's
 * primary view of their history, so a failure reads as a line of muted text rather than the page's
 * destructive alert with a retry.
 */
export function RecentTransactionsSection({ onViewAll, className }: RecentTransactionsSectionProps) {
  const { t } = useTranslation('members');
  const { data, isPending, isError } = usePaymentTransactions({ offset: 0, limit: RECENT_LIMIT });

  const state = isError ? 'error' : isPending ? 'loading' : 'loaded';

  // Withheld only once the query has confirmed there is nothing to see, so the link does not appear
  // a moment after the rows do.
  const isKnownEmpty = data?.totalCount === 0;

  return (
    <section className={cn('mt-8 border-t pt-8', className)} data-slot="recent-transactions">
      <TransactionsTable
        transactions={data?.edges ?? []}
        state={state}
        limit={RECENT_LIMIT}
        title={t('payments.recentTransactions.sectionTitle')}
        description={t('payments.transactions.disclaimer')}
        emptyMessage={t('payments.recentTransactions.emptyState')}
        onViewAll={isKnownEmpty ? undefined : onViewAll}
        viewAllLabel={isKnownEmpty ? undefined : t('payments.recentTransactions.viewAll')}
        errorContent={<p className="text-sm text-muted-foreground">{t('payments.recentTransactions.errorState')}</p>}
      />
    </section>
  );
}
